import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Paper, Typography, CircularProgress, Alert, IconButton, LinearProgress, Button, Box, Card, CardContent } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { translateText } from '../services/translationService';
import { 
  saveReadingProgress, 
  getDocumentProgress, 
  createDocumentIdentifier, 
  clearAutoSave 
} from '../services/progressService';
import { useAuth } from '../contexts/AuthContext';
import UsageSummary from './UsageSummary';
import EnhancedPdfReader from './EnhancedPdfReader';
import LanguageSelector from './LanguageSelector';
import DocumentNavigator from './navigation/DocumentNavigator';
import ProgressBar from './navigation/ProgressBar';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDocumentById, downloadDocument, uploadDocument } from '../services/documentStorageService';
import { trackEvent } from '../services/analyticsService';

const TextReader = ({ initialFile = null, initialDocument = null }) => {
  // Get user context for progress tracking
  const { user, session } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);
  const docId = urlParams.get('docId');
  
  const [text, setText] = useState('');
  const [translation, setTranslation] = useState(null);
  const [selectionTranslation, setSelectionTranslation] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [popupPlacement, setPopupPlacement] = useState('above');
  const [selectionPosition, setSelectionPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [mouseDownTime, setMouseDownTime] = useState(0);
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [vocabCapReached, setVocabCapReached] = useState(false);
  const [vocabCapInfo, setVocabCapInfo] = useState(null);
  const [highlightedWordIndices, setHighlightedWordIndices] = useState(new Set());
  const [fileProcessing, setFileProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');
  const [isPdfFile, setIsPdfFile] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(true);
  const [showTranslationView, setShowTranslationView] = useState(false);
  
  // Navigator state
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [zoomSettings, setZoomSettings] = useState({ zoom: 100, fontSize: 16, lineHeight: 1.5, fitToWidth: false, readingMode: false });
  const [scrollPosition, setScrollPosition] = useState(0);
  const [documentHeight, setDocumentHeight] = useState(0);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [totalPdfPages, setTotalPdfPages] = useState(1);
  const [textPageNumber, setTextPageNumber] = useState(1);
  const [tier, setTier] = useState('free');

  const [librarySaveBanner, setLibrarySaveBanner] = useState(null);
  
  // Progress tracking state
  const [currentDocument, setCurrentDocument] = useState(initialDocument);
  // eslint-disable-next-line no-unused-vars
  const [readingStartTime, setReadingStartTime] = useState(null);
  const [totalReadingTime, setTotalReadingTime] = useState(0);
  const [showUsageSummary, setShowUsageSummary] = useState(false);
  
  // Language state is now managed by the LanguageSelector component
  // We'll read from localStorage directly when needed
  const textRef = useRef(null);
  const zoomTargetRef = useRef(null);
  const progressTimeoutRef = useRef(null);
  const readingSessionRef = useRef(null);
  const handleFileUploadRef = useRef(null);
  const saveCurrentProgressRef = useRef(null);
  const loadDocumentProgressRef = useRef(null);
  const currentDocumentRef = useRef(initialDocument);
  const lastClickedWordRef = useRef(null);
  const wordClickedRef = useRef(false);
  const userRef = useRef(user);
  const hydratedDocIdRef = useRef(null);
  const isHydratingDocIdRef = useRef(false);
  const zoomSettingsRef = useRef(zoomSettings);
  const autoSavedDocHashesRef = useRef(new Set());
  
  // Responsive design hooks - using window width directly
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    currentDocumentRef.current = currentDocument;
  }, [currentDocument]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    zoomSettingsRef.current = zoomSettings;
  }, [zoomSettings]);

  useEffect(() => {
    if (!librarySaveBanner) return;
    const t = setTimeout(() => setLibrarySaveBanner(null), 4500);
    return () => clearTimeout(t);
  }, [librarySaveBanner]);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTier = async () => {
      try {
        const newKey = 'lexory_session_id';
        const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';
        let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
        if (!sessionId) {
          sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        }
        localStorage.setItem(newKey, sessionId);

        const headers = {
          'X-Session-Id': sessionId,
        };
        const token = session?.access_token;
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const resp = await fetch('/api/usage', { headers });
        if (!resp.ok) {
          setTier('free');
          return;
        }
        const data = await resp.json();
        setTier(data?.tier || 'free');
      } catch {
        setTier('free');
      }
    };

    fetchTier();
  }, [session?.access_token, user?.id]);

  // Effect to handle initial file/document loading
  useEffect(() => {
    if (initialFile) {
      console.log('Loading initial file:', initialFile);
      
      // Check if it's a PDF file
      const fileName = initialFile.name || '';
      const isPdf = fileName.toLowerCase().endsWith('.pdf');
      
      if (isPdf) {
        console.log('🔍 [DEBUG] Detected PDF file, setting up PDF reader');
        const objectUrl = URL.createObjectURL(initialFile);
        console.log('🔍 [DEBUG] Setting PDF states - isPdfFile: true, pdfUrl:', objectUrl);
        setIsPdfFile(true);
        setPdfUrl(objectUrl);
        setShowPdfViewer(true);
        setCurrentFileName(fileName);
        console.log('🔍 [DEBUG] PDF states set successfully');
      } else {
        console.log('🔍 [DEBUG] Non-PDF file, handling as text');
        handleFileUploadRef.current?.(initialFile);
      }
    } else if (initialDocument) {
      const isPdf = initialDocument.file_url.toLowerCase().includes('.pdf');
      console.log('🔍 [DEBUG] Document detected:', initialDocument.file_url, 'isPdf:', isPdf);
      
      if (isPdf) {
        console.log('🔍 [DEBUG] Setting PDF states - isPdfFile: true, pdfUrl:', initialDocument.file_url);
        setIsPdfFile(true);
        setPdfUrl(initialDocument.file_url);
        setShowPdfViewer(true);
        setCurrentFileName(initialDocument.title || 'PDF Document');
        console.log('🔍 [DEBUG] PDF states set successfully');
      } else {
        console.log('🔍 [DEBUG] Non-PDF document, setting text content');
        setText(initialDocument.content || '');
        setCurrentFileName(initialDocument.title || 'Document');
      }
    } else {
      console.log('🔍 [DEBUG] No initial file or document provided');
    }
  }, [initialFile, initialDocument]);

  useEffect(() => {
    // Route-based reader: /reader?docId=<uuid>
    const userId = user?.id;
    if (initialFile || !userId) return;

    if (!docId) return;

    if (hydratedDocIdRef.current === docId || isHydratingDocIdRef.current) return;

    let cancelled = false;

    const hydrateFromDocId = async () => {
      try {
        isHydratingDocIdRef.current = true;
        hydratedDocIdRef.current = docId;

        const activeUser = userRef.current;
        if (!activeUser?.id) {
          return;
        }
        setFileProcessing(true);
        setProcessingProgress(10);

        const pageFromUrlRaw = new URLSearchParams(window.location.search).get('page');
        const pageFromUrl = pageFromUrlRaw ? Number.parseInt(pageFromUrlRaw, 10) : null;

        const { document } = await getDocumentById(docId, activeUser);
        if (cancelled) return;
        setProcessingProgress(30);

        trackEvent('reader_opened', {
          source: 'deep_link',
          fileType: document?.file_type || 'unknown',
          stored: true,
        });

        const { url } = await downloadDocument(document, activeUser);
        if (cancelled) return;

        setProcessingProgress(60);
        const resp = await fetch(url);
        const blob = await resp.blob();
        if (cancelled) return;

        const file = new File([blob], document.original_filename, { type: document.file_type });
        setProcessingProgress(90);

        // Use the existing initialFile behavior by simulating the same state transitions
        // (this keeps PDF/text detection behavior unchanged)
        const fileName = file.name || '';
        const isPdf = fileName.toLowerCase().endsWith('.pdf');

        if (isPdf) {
          const objectUrl = URL.createObjectURL(file);
          setIsPdfFile(true);
          setPdfUrl(objectUrl);
          setShowPdfViewer(true);
          setCurrentFileName(fileName);

          if (pageFromUrl && Number.isFinite(pageFromUrl) && pageFromUrl > 0) {
            setCurrentPdfPage(pageFromUrl);
          }
        } else {
          handleFileUploadRef.current?.(file);
        }

        // Ensure progress uses the stable library hash
        setCurrentDocument({
          hash: document.document_hash,
          name: document.original_filename,
          type: document.file_type,
          size: document.file_size,
        });

        setProcessingProgress(100);
      } catch (error) {
        console.error('Error hydrating reader from docId:', error);
        setText('We couldn’t open that document. Please try again.');
      } finally {
        isHydratingDocIdRef.current = false;
        setFileProcessing(false);
        setProcessingProgress(0);
      }
    };

    hydrateFromDocId();

    return () => {
      cancelled = true;
    };
  }, [docId, initialFile, user?.id]);

  // Progress tracking effects
  useEffect(() => {
    // Start reading session when component mounts
    const startTime = Date.now();
    setReadingStartTime(startTime);
    readingSessionRef.current = startTime;

    return () => {
      // Save final progress when component unmounts
      if (currentDocumentRef.current && readingSessionRef.current) {
        const sessionDuration = Math.floor((Date.now() - readingSessionRef.current) / 1000 / 60);
        saveCurrentProgressRef.current?.(sessionDuration);
      }
      // Clear any pending auto-saves
      clearAutoSave();
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
      }
    };
  }, []);

  // Save progress when user leaves the tab / switches apps / closes browser
  useEffect(() => {
    const saveOnLeave = () => {
      if (!currentDocumentRef.current) return;
      const sessionDuration = readingSessionRef.current
        ? Math.floor((Date.now() - readingSessionRef.current) / 1000 / 60) : 0;
      saveCurrentProgressRef.current?.(sessionDuration);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveOnLeave();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', saveOnLeave);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', saveOnLeave);
    };
  }, []);

  // Track scroll position and auto-save progress
  useEffect(() => {
    const handleScroll = () => {
      if (!textRef.current || !currentDocumentRef.current) return;
      
      const element = textRef.current;
      const newScrollPosition = element.scrollTop;
      const newDocumentHeight = element.scrollHeight - element.clientHeight;
      
      setScrollPosition(newScrollPosition);
      setDocumentHeight(newDocumentHeight);
      
      // Debounced auto-save
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
      }
      
      progressTimeoutRef.current = setTimeout(() => {
        const sessionDuration = readingSessionRef.current ? 
          Math.floor((Date.now() - readingSessionRef.current) / 1000 / 60) : 0;
        saveCurrentProgressRef.current?.(sessionDuration);
      }, 2000); // Save every 2 seconds of inactivity
    };

    const element = textRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Load previous progress when document changes
  useEffect(() => {
    if (currentDocument) {
      loadDocumentProgressRef.current?.();
    }
  }, [currentDocument]);
  
  const isVerySmall = windowWidth <= 375;
  const isExtraSmall = windowWidth <= 320;
  const isLargeTextMode = !isPdfFile && !!text && (text.length > 120000 || (currentDocument?.size || 0) > 250000);

  const showUploadSection = !initialFile && !initialDocument && !docId && !fileProcessing && !text && !isPdfFile && !pdfUrl;

  const textPages = React.useMemo(() => {
    if (isPdfFile || !text) return [];

    const pages = [];
    const pageSize = 4500;
    let i = 0;

    while (i < text.length) {
      let end = Math.min(text.length, i + pageSize);

      if (end < text.length) {
        let breakIndex = end;
        while (breakIndex > i && !/\s/.test(text[breakIndex])) {
          breakIndex -= 1;
        }
        if (breakIndex > i + 200) {
          end = breakIndex;
        }
      }

      const chunk = text.slice(i, end);
      pages.push(chunk);

      i = end;
      while (i < text.length && /\s/.test(text[i])) {
        i += 1;
      }
    }

    return pages.length > 0 ? pages : [text];
  }, [isPdfFile, text]);

  const textTotalPages = !isPdfFile ? Math.max(1, textPages.length || 1) : 1;
  const textCurrentPage = !isPdfFile ? Math.min(Math.max(1, textPageNumber), textTotalPages) : 1;
  
  console.log('Window width:', windowWidth, 'isVerySmall:', isVerySmall, 'isExtraSmall:', isExtraSmall);

  const calculatePosition = (rect) => {
    const margin = 10;
    const minSpaceNeeded = 60;
    const x = rect.left + (rect.width / 2);
    let y = rect.top - margin;
    let placement = 'above';
    
    if (rect.top < minSpaceNeeded) {
      y = rect.bottom + margin;
      placement = 'below';
    }
    
    console.log('Popup position calculated:', { x, y, rect, placement });
    return { x, y, placement };
  };

  const calculateDocumentPopupPositionFromViewportRect = (viewportRect) => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const docRect = {
      top: viewportRect.top + scrollTop,
      bottom: viewportRect.bottom + scrollTop,
      left: viewportRect.left + scrollLeft,
      right: viewportRect.right + scrollLeft,
      width: viewportRect.width,
      height: viewportRect.height,
    };

    const pos = calculatePosition(docRect);

    const minSpaceNeeded = 60;
    const placement = viewportRect.top < minSpaceNeeded ? 'below' : 'above';

    return { x: pos.x, y: pos.y, placement };
  };

  // Progress tracking helper functions
  const saveCurrentProgress = useCallback(async (readingTimeMinutes = 0) => {
    if (!currentDocument) return;
    
    try {
      const curPage = isPdfFile ? currentPdfPage : textCurrentPage;
      const totPages = isPdfFile ? totalPdfPages : textTotalPages;
      const progressPercentage = totPages > 0 ? Math.min((curPage / totPages) * 100, 100) : 0;
      
      const progressData = {
        documentHash: currentDocument.hash,
        documentName: currentDocument.name,
        documentType: currentDocument.type,
        currentPage: isPdfFile ? currentPdfPage : textCurrentPage,
        totalPages: isPdfFile ? totalPdfPages : textTotalPages,
        scrollPosition: scrollPosition,
        progressPercentage: progressPercentage,
        readingTimeMinutes: (totalReadingTime + readingTimeMinutes)
      };
      
      await saveReadingProgress(progressData, user);
      console.log('Progress saved:', progressData);
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentDocument, currentPdfPage, isPdfFile, scrollPosition, textCurrentPage, textTotalPages, totalPdfPages, totalReadingTime, user]);

  useEffect(() => {
    if (!isPdfFile) {
      setTextPageNumber(1);
    }
  }, [isPdfFile, text]);

  saveCurrentProgressRef.current = saveCurrentProgress;

  const loadDocumentProgress = useCallback(async () => {
    if (!currentDocument) return;
    
    try {
      const { data: progress } = await getDocumentProgress(currentDocument.hash, user);
      if (progress) {
        setTotalReadingTime(progress.reading_time_minutes || 0);
        setTotalPdfPages(progress.total_pages || 1);
        
        // Restore scroll position for text documents
        if (!isPdfFile && textRef.current && progress.scroll_position) {
          setTimeout(() => {
            textRef.current.scrollTop = progress.scroll_position;
            setScrollPosition(progress.scroll_position);
          }, 100);
        }
        
        // Restore page
        if (progress.current_page) {
          if (isPdfFile) {
            setCurrentPdfPage(progress.current_page);
          } else {
            setTextPageNumber(progress.current_page);
          }

          if (docId) {
            const params = new URLSearchParams(location.search);
            params.set('docId', docId);
            params.set('page', String(progress.current_page));
            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
          }
        }
        
        console.log('Progress loaded:', progress);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  }, [currentDocument, docId, isPdfFile, location.pathname, location.search, navigate, user]);

  loadDocumentProgressRef.current = loadDocumentProgress;

  const createDocumentFromFile = useCallback((file) => {
    const documentId = createDocumentIdentifier(file);
    const document = {
      hash: documentId.hash,
      name: documentId.name,
      type: documentId.type,
      size: file.size
    };
    setCurrentDocument(document);
    return document;
  }, []);

  const clearTranslations = useCallback(() => {
    setTranslation(null);
    setSelectionTranslation(null);
    setHighlightedWordIndices(new Set());
    lastClickedWordRef.current = null;
  }, []);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;

    clearTranslations();
    setLimitExceeded(false);
    setUsageInfo(null);
    setVocabCapReached(false);
    setVocabCapInfo(null);

    setCurrentFileName(file.name || '');
    const doc = createDocumentFromFile(file);

    if (user && doc?.hash && !docId && !autoSavedDocHashesRef.current.has(doc.hash)) {
      autoSavedDocHashesRef.current.add(doc.hash);
      setLibrarySaveBanner({
        severity: 'info',
        message: 'Saving to My Library…',
      });
      (async () => {
        try {
          await uploadDocument(file, user);
          setLibrarySaveBanner({ severity: 'success', message: 'Saved to My Library' });
        } catch (e) {
          const code = e?.code;
          if (code === 'LIBRARY_DOC_CAP_REACHED' || code === 'LIBRARY_SIZE_CAP_REACHED') {
            setLibrarySaveBanner({
              severity: 'warning',
              message: e?.message || 'Library limit reached. Upgrade to save more documents.',
              code,
            });
          } else {
            setLibrarySaveBanner({
              severity: 'warning',
              message: e?.message || 'Could not save to My Library (still opened for reading).',
            });
          }
        }
      })();
    }

    setFileProcessing(true);
    setProcessingProgress(10);
    setText('');

    try {
      const fileName = file.name || '';
      const isPdf = file.type === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

      if (isPdf) {
        const objectUrl = URL.createObjectURL(file);
        setIsPdfFile(true);
        setPdfUrl(objectUrl);
        setShowPdfViewer(true);
        setShowTranslationView(false);
        setCurrentPdfPage(1);
        setProcessingProgress(100);
        return;
      }

      setIsPdfFile(false);
      setPdfUrl(null);
      setShowPdfViewer(false);
      setShowTranslationView(false);

      const reader = new FileReader();
      const textContent = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsText(file);
      });

      setProcessingProgress(90);
      setText(textContent);
      setTextPageNumber(1);
      setProcessingProgress(100);
    } catch (error) {
      console.error('Error processing file:', error);
      setText('We couldn’t open that file. Please try again.');
    } finally {
      setFileProcessing(false);
      setProcessingProgress(0);
    }
  }, [createDocumentFromFile, clearTranslations, user, docId]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  handleFileUploadRef.current = (file) => {
    handleFile(file);
  };

  const handlePdfTextSelection = () => {
    // Keep PDF viewer visible — EnhancedPdfReader handles its own translations
    setShowPdfViewer(true);
    setShowTranslationView(false);
  };

  const getSelectionHighlightIndices = () => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;
      if (!textRef.current) return null;

      const rangeForHighlight = selection.getRangeAt(0);
      const spans = Array.from(textRef.current.querySelectorAll('.word'));
      const indices = [];

      spans.forEach((el) => {
        try {
          if (rangeForHighlight.intersectsNode(el)) {
            const idx = el.dataset.wordIdx;
            if (idx !== undefined) {
              const n = Number(idx);
              if (!Number.isNaN(n)) indices.push(n);
            }
          }
        } catch {
          return;
        }
      });

      return indices.length > 0 ? new Set(indices) : null;
    } catch {
      return null;
    }
  };

  const renderPlainText = (plain) => {
    return (
      <Typography
        component="pre"
        variant="body1"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
      >
        {plain}
      </Typography>
    );
  };

  const renderWords = (textToRender) => {
    if (!textToRender) return null;

    const parts = String(textToRender).split(/([\s,.!?;:'"-]+|\n+)/);
    let wordIdx = 0;

    return (
      <div
        onMouseDown={() => {
          setMouseDownTime(Date.now());
          setIsDragging(false);
        }}
        onMouseMove={() => {
          if (mouseDownTime) {
            setIsDragging(true);
          }
        }}
      >
        {parts.map((part, i) => {
          if (!part) return null;

          const isSeparator = /^[\s,.!?;:'"-]+$/.test(part) || /^\n+$/.test(part);
          if (isSeparator) {
            return <span key={i}>{part}</span>;
          }

          const myIdx = wordIdx;
          wordIdx += 1;
          const isHighlighted = highlightedWordIndices?.has?.(myIdx);

          return (
            <span
              key={i}
              className={`word${isHighlighted ? ' highlighted' : ''}`}
              data-word-idx={String(myIdx)}
              onClick={(e) => handleWordClickWithLang(e, part, myIdx)}
            >
              {part}
            </span>
          );
        })}
      </div>
    );
  };

  const expandSelectionToWords = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return null;

    const range = selection.getRangeAt(0);
    const originalText = selection.toString().trim();
    if (!originalText) return null;

    try {
      // Get the start and end containers
      let startContainer = range.startContainer;
      let endContainer = range.endContainer;
      let startOffset = range.startOffset;
      let endOffset = range.endOffset;

      console.log('Original selection:', { originalText, startOffset, endOffset });

      // Function to expand to word boundaries in plain text
      const expandToWordBoundaries = (container, startOff, endOff) => {
        if (container.nodeType !== Node.TEXT_NODE) {
          return { start: startOff, end: endOff };
        }
        
        const textContent = container.textContent;
        let newStart = startOff;
        let newEnd = endOff;
        
        // Expand start to word boundary
        while (newStart > 0 && /\w/.test(textContent[newStart - 1])) {
          newStart--;
        }
        
        // Expand end to word boundary
        while (newEnd < textContent.length && /\w/.test(textContent[newEnd])) {
          newEnd++;
        }
        
        console.log('Expanded boundaries:', { original: { start: startOff, end: endOff }, expanded: { start: newStart, end: newEnd } });
        return { start: newStart, end: newEnd };
      };

      // Check if we're dealing with word spans (structured text)
      let startWordSpan = null;
      let endWordSpan = null;
      
      // Try to find parent word spans
      if (startContainer.nodeType === Node.TEXT_NODE) {
        let parent = startContainer.parentNode;
        while (parent && !parent.classList?.contains('word') && parent !== document.body) {
          parent = parent.parentNode;
        }
        if (parent && parent.classList?.contains('word')) {
          startWordSpan = parent;
        }
      }
      
      if (endContainer.nodeType === Node.TEXT_NODE) {
        let parent = endContainer.parentNode;
        while (parent && !parent.classList?.contains('word') && parent !== document.body) {
          parent = parent.parentNode;
        }
        if (parent && parent.classList?.contains('word')) {
          endWordSpan = parent;
        }
      }

      // If we have word spans, use them for expansion
      if (startWordSpan && endWordSpan) {
        console.log('Using word span expansion');
        
        // Find all word spans between start and end
        const wordSpans = [];
        let current = startWordSpan;
        
        // Add the start span
        wordSpans.push(current);
        
        // Find spans between start and end
        while (current && current !== endWordSpan) {
          current = current.nextElementSibling;
          if (current && current.classList?.contains('word')) {
            wordSpans.push(current);
            if (current === endWordSpan) break;
          }
        }
        
        // Create new range encompassing all word spans
        const newRange = document.createRange();
        newRange.setStartBefore(wordSpans[0]);
        newRange.setEndAfter(wordSpans[wordSpans.length - 1]);
        
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        const expandedText = newRange.toString().trim();
        console.log('Word span expansion result:', expandedText);
        
        return {
          text: expandedText,
          range: newRange
        };
      } 
      // Handle plain text (like PDF-extracted text)
      else {
        console.log('Using plain text expansion');
        
        // For same container, expand within that container
        if (startContainer === endContainer) {
          const boundaries = expandToWordBoundaries(startContainer, startOffset, endOffset);
          
          const newRange = document.createRange();
          newRange.setStart(startContainer, boundaries.start);
          newRange.setEnd(endContainer, boundaries.end);
          
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          const expandedText = newRange.toString().trim();
          console.log('Plain text expansion result:', expandedText);
          
          return {
            text: expandedText,
            range: newRange
          };
        }
        // For different containers, expand each boundary separately
        else {
          const startBoundaries = expandToWordBoundaries(startContainer, startOffset, startContainer.textContent.length);
          const endBoundaries = expandToWordBoundaries(endContainer, 0, endOffset);
          
          const newRange = document.createRange();
          newRange.setStart(startContainer, startBoundaries.start);
          newRange.setEnd(endContainer, endBoundaries.end);
          
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          const expandedText = newRange.toString().trim();
          console.log('Multi-container expansion result:', expandedText);
          
          return {
            text: expandedText,
            range: newRange
          };
        }
      }
    } catch (error) {
      console.error('Error expanding selection to words:', error);
      return {
        text: originalText,
        range: range
      };
    }
  };

  const handleTextSelection = async () => {
    handleTextSelectionWithLang();
  };

  const handleTextSelectionWithLang = async () => {
    // Skip if a word click just fired (prevents race condition with mouseup)
    if (wordClickedRef.current) return;

    // Get the selected text
    const selection = window.getSelection();
    let text = selection.toString().trim();
    
    // If no text is selected, return
    if (!text) {
      return;
    }
    
    // Try to expand selection to complete word boundaries
    const expandedSelection = expandSelectionToWords();
    if (expandedSelection && expandedSelection.text) {
      text = expandedSelection.text;
      console.log('Selection expanded to word boundaries:', { original: selection.toString().trim(), expanded: text });
    }
    
    // Check if this is a multi-word selection (contains spaces or multiple words)
    const isMultiWord = text.includes(' ') || text.split(/\s+/).length > 1;
    
    console.log('Selection analysis:', { text, isMultiWord, length: text.length });
    
    // For single words, be more permissive about treating as selection
    // Only skip if it's clearly a programmatic word click (not user selection)
    if (!isMultiWord && text.length < 20) {
      // Check if this was a deliberate selection by looking at selection range
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const startContainer = range.startContainer;
        const endContainer = range.endContainer;
        
        console.log('Single word selection check:', {
          startContainer: startContainer.nodeType,
          endContainer: endContainer.nodeType,
          startOffset: range.startOffset,
          endOffset: range.endOffset,
          textContent: startContainer.textContent
        });
        
        // Only skip if it's a perfect word span selection (likely from word click)
        // Allow partial selections or selections that span multiple nodes
        if (startContainer === endContainer && 
            startContainer.nodeType === Node.TEXT_NODE &&
            range.startOffset === 0 && 
            range.endOffset === startContainer.textContent.length &&
            startContainer.parentNode?.classList?.contains('word')) {
          console.log('Skipping - appears to be word click, not selection');
          return;
        }
      }
    }
    
    console.log('Proceeding with translation for:', { text, isMultiWord });

    const selectedWordCount = (text.match(/\S+/g) || []).length;
    const maxSelectableWords = tier === 'premium' ? 30 : 15;
    
    // Get the position for the translation popup
    let rect;
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      rect = range.getBoundingClientRect();
      console.log('Selection rect:', { 
        rect, 
        width: rect?.width, 
        height: rect?.height,
        top: rect?.top,
        left: rect?.left,
        bottom: rect?.bottom,
        right: rect?.right
      });
    } else {
      console.log('No selection range found');
    }
    
    if (!rect || rect.width === 0) {
      console.log('Invalid rect, aborting translation:', { rect });
      return;
    }
    
    // Add scroll offset to ensure proper positioning
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Adjust rect coordinates to account for scroll position
    const adjustedRect = {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
      right: rect.right + scrollLeft,
      bottom: rect.bottom + scrollTop,
      width: rect.width,
      height: rect.height
    };
    
    console.log('Adjusted rect with scroll:', adjustedRect);
    
    const position = calculatePosition(adjustedRect);
    console.log('Final calculated position:', position);
    setSelectionPosition(position);
    
    // If the text is the same as the last translated selection, toggle it off
    if (text && selectionTranslation && text === window.lastTranslatedSelection) {
      setSelectionTranslation(null);
      setHighlightedWordIndices(new Set());
      window.lastTranslatedSelection = null;
      return;
    }

    if (selectedWordCount > maxSelectableWords) {
      setTranslation(null);
      setSelectionTranslation(`Please select up to ${maxSelectableWords} words to translate at a time.`);
      setHighlightedWordIndices(new Set());
      return;
    }

    try {
      // Get source and target languages from localStorage
      const sourceLang = localStorage.getItem('sourceLang') || 'auto';
      const targetLang = localStorage.getItem('targetLang') || 'en';
      
      console.log('Selection translation request:', {
        text: text,
        textLength: text.length,
        sourceLang,
        targetLang
      });
      
      console.log('Calling translateText API...');
      const result = await translateText(text, sourceLang, targetLang, {
        trigger: 'selection',
        contentType: isPdfFile ? 'pdf' : 'text',
      });
      console.log('Translation API result:', result);
      
      if (result.limitExceeded) {
        if (result.usage.tier === 'free') {
          setLimitExceeded(true);
          setUsageInfo(result.usage);
          setSelectionTranslation('Upgrade to premium to translate more text.');
        } else {
          setLimitExceeded(true);
          setUsageInfo(result.usage);
          setSelectionTranslation('Monthly limit reached.');
        }
      } else if (result.requestTooLarge) {
        const max = result.usage.maxWordsPerRequest || 30;
        setSelectionTranslation(`That selection is too long. Try up to ${max} words.`);
      } else {
        setSelectionTranslation(result.translatedText);
        window.lastTranslatedSelection = text; // Store the last translated selection

        if (result.saveResult?.reason === 'VOCAB_CAP_REACHED') {
          setVocabCapReached(true);
          setVocabCapInfo({ limit: result.saveResult.limit || 300 });
        }

        const selectionHighlightIndices = getSelectionHighlightIndices(result);
        if (selectionHighlightIndices) {
          setHighlightedWordIndices(selectionHighlightIndices);
        } else {
          setHighlightedWordIndices(new Set());
        }

        // Clear browser selection to reveal the styled word boxes
        window.getSelection()?.removeAllRanges();
      }
    } catch (error) {
      console.error('Translation error:', error);
      setSelectionTranslation('Couldn’t translate that. Please try again.');
    }
  };

  const handleWordClickWithLang = async (event, word, wordIndex) => {
    event.stopPropagation();
    wordClickedRef.current = true;
    setTimeout(() => { wordClickedRef.current = false; }, 300);
    
    // Check if the clicked word is the same as the currently translated word
    // If so, toggle off the translation
    if (translation && word === lastClickedWordRef.current) {
      setTranslation(null);
      setHighlightedWordIndices(new Set());
      lastClickedWordRef.current = null;
      return;
    }
    
    window.getSelection()?.removeAllRanges();
    setSelectionTranslation(null);
    setIsLoading(true);

    // Highlight the clicked word immediately
    if (typeof wordIndex === 'number') {
      setHighlightedWordIndices(new Set([wordIndex]));
    }
    
    const rect = event.target.getBoundingClientRect();
    const position = calculateDocumentPopupPositionFromViewportRect(rect);
    setPopupPosition({ x: position.x, y: position.y });
    setPopupPlacement(position.placement);

    try {
      // Get source and target languages from localStorage
      const sourceLang = localStorage.getItem('sourceLang') || 'auto';
      const targetLang = localStorage.getItem('targetLang') || 'en';
      
      console.log('Translation request:', {
        word,
        sourceLang,
        targetLang
      });
      
      const result = await translateText(word, sourceLang, targetLang, {
        trigger: 'click',
        contentType: isPdfFile ? 'pdf' : 'text',
      });
      
      if (result.limitExceeded) {
        setLimitExceeded(true);
        setUsageInfo(result.usage);
        setTranslation(result?.usage?.tier === 'premium' ? 'Monthly limit reached.' : 'That’s all for today.');
        lastClickedWordRef.current = null;
      } else {
        setTranslation(result.translatedText);
        lastClickedWordRef.current = word;

        if (result?.saveResult?.reason === 'VOCAB_CAP_REACHED') {
          setVocabCapReached(true);
          setVocabCapInfo({ limit: result?.saveResult?.limit || 300 });
        }
      }
    } catch (error) {
      console.error('Translation error:', error);
      setTranslation('Couldn’t translate that. Please try again.');
      lastClickedWordRef.current = null;
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const translateWordAtRect = async (word, rect) => {
    if (!word) return;

    if (translation && lastClickedWordRef.current === word) {
      setTranslation(null);
      lastClickedWordRef.current = null;
      return;
    }

    setSelectionTranslation(null);
    setIsLoading(true);

    const position = calculateDocumentPopupPositionFromViewportRect(rect);
    setPopupPosition({ x: position.x, y: position.y });
    setPopupPlacement(position.placement);

    try {
      const sourceLang = localStorage.getItem('sourceLang') || 'auto';
      const targetLang = localStorage.getItem('targetLang') || 'en';

      const result = await translateText(word, sourceLang, targetLang, {
        trigger: 'click',
        contentType: 'text',
      });

      if (result.limitExceeded) {
        setLimitExceeded(true);
        setUsageInfo(result.usage);
        setTranslation(result?.usage?.tier === 'premium' ? 'Monthly limit reached.' : 'That’s all for today.');
        lastClickedWordRef.current = null;
      } else {
        setTranslation(result.translatedText);
        lastClickedWordRef.current = word;

        if (result?.saveResult?.reason === 'VOCAB_CAP_REACHED') {
          setVocabCapReached(true);
          setVocabCapInfo({ limit: result?.saveResult?.limit || 300 });
        }
      }
    } catch (error) {
      console.error('Translation error:', error);
      setTranslation('Couldn’t translate that. Please try again.');
      lastClickedWordRef.current = null;
    } finally {
      setIsLoading(false);
    }
  };

  const getCaretFromPoint = (x, y) => {
    if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(x, y);
      if (!range) return null;
      return { node: range.startContainer, offset: range.startOffset };
    }

    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(x, y);
      if (!pos) return null;
      return { node: pos.offsetNode, offset: pos.offset };
    }

    return null;
  };

  const isWordChar = (ch) => {
    if (!ch) return false;
    try {
      return /[\p{L}\p{N}_]/u.test(ch);
    } catch {
      return /\w/.test(ch);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getWordAtPoint = (x, y) => {
    const caret = getCaretFromPoint(x, y);
    if (!caret) return null;

    let { node, offset } = caret;
    if (!node) return null;

    if (node.nodeType !== Node.TEXT_NODE) {
      const child = node.childNodes?.[Math.max(0, Math.min(offset, (node.childNodes?.length || 1) - 1))];
      if (child && child.nodeType === Node.TEXT_NODE) {
        node = child;
        offset = Math.min(offset, node.textContent?.length || 0);
      } else {
        return null;
      }
    }

    const textContent = node.textContent || '';
    if (!textContent) return null;

    let start = Math.max(0, Math.min(offset, textContent.length));
    let end = start;

    if (start > 0 && !isWordChar(textContent[start]) && isWordChar(textContent[start - 1])) {
      start = start - 1;
      end = start;
    }

    while (start > 0 && isWordChar(textContent[start - 1])) start -= 1;
    while (end < textContent.length && isWordChar(textContent[end])) end += 1;

    const word = textContent.slice(start, end).trim();
    if (!word) return null;
    return word;
  };

  // Function to dismiss the limit notification
  const dismissLimitNotification = () => {
    setLimitExceeded(false);
  };

  const dismissVocabCapNotification = () => {
    setVocabCapReached(false);
    setVocabCapInfo(null);
  };

  const buildUpgradePath = () => {
    const planPath = '/usage?plan=yearly';
    if (user) return planPath;
    return `/login?redirect=${encodeURIComponent(planPath)}`;
  };

  const handleUpgrade = () => {
    window.location.assign(buildUpgradePath());
  };

  const handleSeeAllPlans = () => {
    window.location.assign('/usage');
  };

  const handleViewUsage = () => {
    window.location.assign('/usage');
  };

  // Display limit exceeded notification
  const renderLimitExceededNotification = () => {
    if (!limitExceeded || !usageInfo) return null;

    if (usageInfo?.tier === 'premium') {
      const isHard = usageInfo?.code === 'HARD_LIMIT_EXCEEDED';
      return (
        <Alert
          severity={isHard ? 'warning' : 'info'}
          sx={{
            position: 'fixed',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            maxWidth: '600px',
            zIndex: 1000,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={dismissLimitNotification}
            >
              ✕
            </IconButton>
          }
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {isHard ? 'Monthly limit reached' : 'Monthly limit reached'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You’ve hit your monthly translation limit. You can keep reading—translations will be available again next month.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Button variant="outlined" size="small" onClick={handleViewUsage}>
              View usage
            </Button>
            <Button variant="text" size="small" onClick={dismissLimitNotification}>
              Continue reading
            </Button>
          </Box>
        </Alert>
      );
    }

    const limit = usageInfo?.limitWords ?? usageInfo?.limit ?? null;
    const used = usageInfo?.usedWords ?? usageInfo?.todayUsage ?? null;
    const usagePercentage = (typeof used === 'number' && typeof limit === 'number' && limit > 0)
      ? Math.min(100, (used / limit) * 100)
      : 100;

    return (
      <Alert 
        severity="warning" 
        sx={{ 
          position: 'fixed', 
          top: '10px', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          width: '80%', 
          maxWidth: '600px',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={dismissLimitNotification}
          >
            ✕
          </IconButton>
        }
      >
        <Typography variant="subtitle1" fontWeight="bold">
          You hit today’s free limit
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Upgrade to keep translating and keep saving words while you read.
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={usagePercentage} 
          sx={{ 
            mt: 1, 
            mb: 1, 
            height: 8, 
            borderRadius: 4,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: usagePercentage >= 100 ? '#f44336' : '#ff9800'
            }
          }} 
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Button variant="contained" size="small" onClick={handleUpgrade} sx={{ fontWeight: 800 }}>
            Upgrade (Most popular)
          </Button>
          <Button variant="outlined" size="small" onClick={handleSeeAllPlans}>
            See plans
          </Button>
          <Button variant="text" size="small" onClick={dismissLimitNotification}>
            Continue reading
          </Button>
        </Box>
      </Alert>
    );
  };

  const renderVocabCapNotification = () => {
    if (!vocabCapReached) return null;

    const limit = vocabCapInfo?.limit || 300;

    return (
      <Alert
        severity="info"
        sx={{
          position: 'fixed',
          top: limitExceeded ? '104px' : '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80%',
          maxWidth: '600px',
          zIndex: 999,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={dismissVocabCapNotification}
          >
            ✕
          </IconButton>
        }
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Vocabulary pack full
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          You’ve saved {limit} words on Free. You can still review/export them, but new words won’t be saved until you upgrade.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" onClick={handleUpgrade} sx={{ fontWeight: 800 }}>
            Upgrade to keep saving
          </Button>
          <Button variant="outlined" size="small" onClick={() => window.location.assign('/saved-translations')}>
            Open My Words
          </Button>
        </Box>
      </Alert>
    );
  };

  // Navigator functionality handlers
  const handleSearch = (query, results, searchIndex = -1) => {
    setSearchResults(results || []);
    setCurrentSearchIndex(searchIndex);
    
    // Highlight search results in the document (with debouncing to prevent infinite loops)
    if (!isLargeTextMode && results && results.length > 0 && textRef.current && query) {
      // Use setTimeout to prevent infinite re-renders
      setTimeout(() => {
        try {
          // Clear previous highlights
          const existingHighlights = textRef.current.querySelectorAll('.search-highlight');
          existingHighlights.forEach(el => {
            el.classList.remove('search-highlight', 'search-highlight-current');
          });
          
          // Add new highlights
          const words = textRef.current.querySelectorAll('.word');
          let highlightCount = 0;
          
          words.forEach(word => {
            if (word.textContent.toLowerCase().includes(query.toLowerCase()) && highlightCount < 100) {
              word.classList.add('search-highlight');
              if (highlightCount === searchIndex) {
                word.classList.add('search-highlight-current');
                word.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              highlightCount++;
            }
          });
        } catch (error) {
          console.error('Error highlighting search results:', error);
        }
      }, 100);
    }
  };

  const handlePageChange = (page, totalPages) => {
    window.speechSynthesis.cancel();
    if (isPdfFile) {
      // Handle PDF page changes
      setCurrentPdfPage(page);
      if (totalPages) {
        setTotalPdfPages(totalPages);
      }

      if (docId) {
        const params = new URLSearchParams(location.search);
        params.set('docId', docId);
        params.set('page', String(page));
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      }

      // Persist page change to DB
      if (currentDocumentRef.current) {
        const sessionDuration = readingSessionRef.current
          ? Math.floor((Date.now() - readingSessionRef.current) / 1000 / 60) : 0;
        saveCurrentProgressRef.current?.(sessionDuration);
      }
    } else {
      const safeTotal = totalPages && totalPages > 0 ? totalPages : textTotalPages;
      const targetPage = Math.min(Math.max(1, page), safeTotal || 1);
      setTextPageNumber(targetPage);

      if (textRef.current) {
        textRef.current.scrollTop = 0;
        setScrollPosition(0);
      }

      if (docId) {
        const params = new URLSearchParams(location.search);
        params.set('docId', docId);
        params.set('page', String(targetPage));
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      }

      // Persist page change to DB
      if (currentDocumentRef.current) {
        const sessionDuration = readingSessionRef.current
          ? Math.floor((Date.now() - readingSessionRef.current) / 1000 / 60) : 0;
        saveCurrentProgressRef.current?.(sessionDuration);
      }
    }
  };

  const previousTextPage = () => {
    clearTranslations();
    const newPage = Math.max(1, textCurrentPage - 1);
    handlePageChange(newPage, textTotalPages);
  };

  const nextTextPage = () => {
    clearTranslations();
    const newPage = Math.min(textTotalPages, textCurrentPage + 1);
    handlePageChange(newPage, textTotalPages);
  };

  const renderTextViewer = () => {
    const pageText = textPages[textCurrentPage - 1] || '';

    const zoomLevels = [50, 75, 100, 125, 150, 175, 200, 250, 300];

    const zoomIn = () => {
      const current = zoomSettings.zoom || 100;
      const currentIndex = zoomLevels.findIndex((level) => level >= current);
      const safeIndex = currentIndex === -1 ? zoomLevels.indexOf(100) : currentIndex;
      const nextIndex = Math.min(safeIndex + 1, zoomLevels.length - 1);
      const nextZoom = zoomLevels[nextIndex];
      handleZoomChange({ ...zoomSettings, zoom: nextZoom, fitToWidth: false });
    };

    const zoomOut = () => {
      const current = zoomSettings.zoom || 100;
      const currentIndex = zoomLevels.findIndex((level) => level >= current);
      const safeIndex = currentIndex === -1 ? zoomLevels.indexOf(100) : currentIndex;
      const prevIndex = Math.max(safeIndex - 1, 0);
      const prevZoom = zoomLevels[prevIndex];
      handleZoomChange({ ...zoomSettings, zoom: prevZoom, fitToWidth: false });
    };

    return (
      <div className="pdf-text-reader" onClick={(e) => { if (e.target === e.currentTarget) clearTranslations(); }}>
        <Card>
          <CardContent>
            <Box className="pdf-text-header">
              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <IconButton size="small" onClick={zoomOut} aria-label="Zoom out">
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={zoomIn} aria-label="Zoom in">
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Box>

              <div
                className="page-info-navigation"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body2" color="textSecondary" style={{ margin: '0 10px' }}>
                  Page {textCurrentPage} of {textTotalPages}
                </Typography>
              </div>

              <div className="page-controls" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <Button
                  variant="outlined"
                  onClick={previousTextPage}
                  disabled={textCurrentPage <= 1}
                  size="small"
                >
                  ← Previous
                </Button>
                <Button
                  variant="outlined"
                  onClick={nextTextPage}
                  disabled={textCurrentPage >= textTotalPages}
                  size="small"
                >
                  Next →
                </Button>
              </div>
            </Box>
          </CardContent>
        </Card>

        <Paper
          ref={zoomTargetRef}
          elevation={1}
          style={{ padding: '1rem', marginTop: '1rem', minHeight: '400px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {pageText ? renderWords(pageText) : (
            <Typography variant="body1" color="textSecondary" align="center">
              No text to translate yet
            </Typography>
          )}
        </Paper>
      </div>
    );
  };

  const handleZoomChange = useCallback((settings) => {
    // Prevent infinite loops by checking if settings actually changed
    if (JSON.stringify(settings) === JSON.stringify(zoomSettingsRef.current)) {
      return;
    }

    zoomSettingsRef.current = settings;
    setZoomSettings(settings);

    // Apply zoom settings to the document with debouncing
    setTimeout(() => {
      const zoomEl = zoomTargetRef.current || textRef.current;
      if (zoomEl) {
        try {
          const textContainer = zoomEl;
          const zoomFactor = (settings.zoom || 100) / 100;
          textContainer.style.fontSize = `${(settings.fontSize || 16) * zoomFactor}px`;
          textContainer.style.lineHeight = String(settings.lineHeight || 1.5);
          textContainer.style.transform = '';
          textContainer.style.transformOrigin = '';

          if (settings.fitToWidth) {
            textContainer.style.width = '100%';
            textContainer.style.maxWidth = '100%';
          }

          if (settings.readingMode) {
            textContainer.style.maxWidth = '800px';
            textContainer.style.margin = '0 auto';
            textContainer.style.padding = '2rem';
          }
        } catch (error) {
          console.error('Error applying zoom settings:', error);
        }
      }
    }, 50);
  }, []);

  // Track scroll position for progress bar
  useEffect(() => {
    const handleScroll = () => {
      if (textRef.current) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        setScrollPosition(scrollTop);
        setDocumentHeight(scrollHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="reader-container" onClick={(e) => { if (e.target === e.currentTarget) clearTranslations(); }}>
      {renderLimitExceededNotification()}
      {renderVocabCapNotification()}
      <div className="app-header" onClick={(e) => e.stopPropagation()}>
        {/* Decorative language flags */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: '4px', sm: '8px' },
            mb: 0.75,
            opacity: 0.85,
            userSelect: 'none',
            flexWrap: 'wrap',
          }}
        >
          {[
            { code: 'us', title: 'English' },
            { code: 'es', title: 'Spanish' },
            { code: 'br', title: 'Portuguese' },
            { code: 'fr', title: 'French' },
            { code: 'de', title: 'German' },
            { code: 'it', title: 'Italian' },
            { code: 'jp', title: 'Japanese' },
            { code: 'cn', title: 'Chinese' },
            { code: 'kr', title: 'Korean' },
            { code: 'sa', title: 'Arabic' },
            { code: 'ru', title: 'Russian' },
            { code: 'in', title: 'Hindi' },
          ].map((f) => (
            <img
              key={f.code}
              src={`https://flagcdn.com/w40/${f.code}.png`}
              srcSet={`https://flagcdn.com/w80/${f.code}.png 2x`}
              alt={f.title}
              title={f.title}
              width="24"
              height="18"
              style={{ borderRadius: 2 }}
            />
          ))}
        </Box>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ textAlign: 'center', mb: 0.5, fontStyle: 'italic', opacity: 0.9 }}
        >
          Read naturally. Click anything you don't understand.
        </Typography>
        
        {/* Usage Summary */}
        <div style={{ marginTop: '0.25rem' }}>
          <Button
            variant="text"
            size="small"
            sx={{ textTransform: 'none', opacity: 0.85 }}
            onClick={() => setShowUsageSummary(prev => !prev)}
          >
            {showUsageSummary ? 'Hide today’s reading stats' : 'Show today’s reading stats'}
          </Button>
          {showUsageSummary && <UsageSummary />}
        </div>

        {/* Language Selector Component */}
        <LanguageSelector />

        {!!currentFileName && (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mt: 0.5,
              textAlign: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={currentFileName}
          >
            {currentFileName}
          </Typography>
        )}

        {librarySaveBanner && (
          <Alert
            severity={librarySaveBanner.severity || 'info'}
            sx={{ mt: 1, maxWidth: 920, mx: 'auto' }}
            action={
              librarySaveBanner.code === 'LIBRARY_DOC_CAP_REACHED' || librarySaveBanner.code === 'LIBRARY_SIZE_CAP_REACHED' ? (
                <Button size="small" variant="contained" onClick={handleUpgrade} sx={{ fontWeight: 800 }}>
                  Upgrade
                </Button>
              ) : null
            }
          >
            {librarySaveBanner.message}
          </Alert>
        )}
      </div>

      {/* Only show upload section if no file was passed from document library */}
      {showUploadSection && (
        <div
          className={`upload-section ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="file"
            accept=".txt,.pdf,application/pdf,text/plain"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-upload"
          />
          <div className="upload-icon">📄</div>
          <label htmlFor="file-upload">
            <Typography variant="h6" color="primary" style={{ cursor: 'pointer' }}>
              Drop in something to read
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to choose a PDF or text file
            </Typography>
          </label>
        </div>
      )}

      <Paper
        ref={textRef}
        elevation={0}
        onMouseUp={(e) => {
          // Add a small delay to ensure selection is properly established
          setTimeout(() => {
            handleTextSelection();
          }, 10);
        }}
        className="text-content"
        onClick={(e) => {
          // Only clear if clicking directly on the Paper (not on a word)
          if (e.target === e.currentTarget) {
            clearTranslations();
          }
        }}
      >
        <div className="text-controls">
        </div>
        
        {fileProcessing ? (
          <div className="processing-container">
            <CircularProgress size={40} />
            <Typography variant="body1" style={{ marginTop: '1rem' }}>
              Processing {currentFileName}...
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={processingProgress} 
              sx={{ width: '80%', marginTop: '1rem' }} 
            />
          </div>
        ) : isPdfFile && showPdfViewer ? (
          <div className="pdf-viewer-container">
            <div>
              <Alert severity="success" style={{ marginBottom: '1rem' }}>
                Select text in the PDF to translate it.
              </Alert>
              {/* Debug info for conditional rendering */}
              {console.log(' [DEBUG] Rendering EnhancedPdfReader - isPdfFile:', isPdfFile, 'showPdfViewer:', showPdfViewer, 'pdfUrl:', pdfUrl ? 'SET' : 'NOT SET')}
              {/* Use key prop to prevent unnecessary re-mounting */}
              <EnhancedPdfReader 
                key={pdfUrl} // Only re-mount when PDF URL actually changes
                pdfUrl={pdfUrl} 
                onTextSelect={handlePdfTextSelection}
                onPageChange={handlePageChange}
                currentPage={currentPdfPage}
                totalPages={totalPdfPages}
              />
            </div>
          </div>
        ) : isPdfFile && !showPdfViewer && showTranslationView ? (
          <div>
            <div className="translation-mode-header">
              <Typography variant="body2" color="textSecondary">
                <em>Reading text from PDF with Lexory assistance. <Button size="small" onClick={() => setShowPdfViewer(true)}>Return to PDF viewer</Button></em>
              </Typography>
            </div>
            {text ? (isLargeTextMode ? renderPlainText(text) : renderWords(text)) : (
              <Typography variant="body1" color="textSecondary" align="center">
                No text to translate yet
              </Typography>
            )}
          </div>
        ) : text ? (
          renderTextViewer()
        ) : (
          <Typography variant="body1" color="textSecondary" align="center">
            Your reading will appear here.
          </Typography>
        )}
      </Paper>

      {(translation || isLoading) && (
        <div
          className="translation-container translation-popup"
          style={{
            position: 'absolute',
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            transform: popupPlacement === 'below' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            zIndex: 1000
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} style={{ margin: '8px auto', display: 'block', pointerEvents: 'none' }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'auto' }}>
              <Typography variant="body1">{translation}</Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const srcWord = lastClickedWordRef.current || '';
                  if (!srcWord) return;
                  const lang = localStorage.getItem('sourceLang') || 'auto';
                  const langMap = { zh: 'zh-CN', pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU', ar: 'ar-SA', hi: 'hi-IN' };
                  if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); return; }
                  const u = new SpeechSynthesisUtterance(srcWord);
                  if (lang !== 'auto') u.lang = langMap[lang] || lang;
                  window.speechSynthesis.speak(u);
                }}
                sx={{
                  p: 0.5,
                  flexShrink: 0,
                  color: 'primary.main',
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '50%',
                  boxShadow: '0 0 6px 1px rgba(102, 126, 234, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                    boxShadow: '0 0 10px 3px rgba(102, 126, 234, 0.35)',
                  },
                }}
              >
                <VolumeUpIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
        </div>
      )}

      {selectionTranslation && (
        <div
          className="translation-container selection-translation"
          style={{
            position: 'absolute',
            left: `${selectionPosition.x}px`,
            top: `${selectionPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 1000
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'auto' }}>
            <Typography variant="body1">{selectionTranslation}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const srcText = window.lastTranslatedSelection || '';
                if (!srcText) return;
                const lang = localStorage.getItem('sourceLang') || 'auto';
                const langMap = { zh: 'zh-CN', pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU', ar: 'ar-SA', hi: 'hi-IN' };
                if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); return; }
                const u = new SpeechSynthesisUtterance(srcText);
                if (lang !== 'auto') u.lang = langMap[lang] || lang;
                window.speechSynthesis.speak(u);
              }}
              sx={{
                p: 0.5,
                flexShrink: 0,
                color: 'primary.main',
                bgcolor: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '50%',
                boxShadow: '0 0 6px 1px rgba(102, 126, 234, 0.2)',
                '&:hover': {
                  bgcolor: 'rgba(102, 126, 234, 0.2)',
                  boxShadow: '0 0 10px 3px rgba(102, 126, 234, 0.35)',
                },
              }}
            >
              <VolumeUpIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </div>
      )}

      {/* Progress Bar - show reading progress */}
      {currentDocument && (text || isPdfFile) && !fileProcessing && (
        <ProgressBar
          currentPage={isPdfFile ? currentPdfPage : textCurrentPage}
          totalPages={isPdfFile ? totalPdfPages : textTotalPages}
          scrollPosition={scrollPosition}
          documentHeight={documentHeight}
          documentType={isPdfFile ? 'pdf' : 'text'}
        />
      )}

      {/* Document Navigator - only show when there's content and no processing */}
      {(text || isPdfFile) && !fileProcessing && (
        <DocumentNavigator
          text={text || ''}
          currentPage={isPdfFile ? currentPdfPage : textCurrentPage}
          totalPages={isPdfFile ? totalPdfPages : textTotalPages}
          onPageChange={handlePageChange}
          onZoomChange={handleZoomChange}
          onSearch={handleSearch}
          searchResults={searchResults || []}
          currentSearchIndex={currentSearchIndex || -1}
          documentType={isPdfFile ? 'pdf' : 'text'}
          scrollPosition={scrollPosition || 0}
          documentHeight={documentHeight || 0}
        />
      )}
    </div>
  );
};

export default TextReader;
